/**
 * LLM tool definitions Bruno exposes to OpenRouter via function calling, plus
 * Zod schemas to validate tool-call arguments coming back from the model.
 *
 * The widget executes these tool calls. `highlight_element` and `explain` are
 * autonomous; `propose_action` and `navigate_hint` require user consent. After
 * approval, propose_action is previewed with a hint + highlight before it runs.
 */
import { z } from 'zod';
export const HighlightArgs = z.object({
    selector: z.string().min(1),
    label: z.string().max(100).optional(),
    instruction: z.string().max(500),
});
export const ExplainArgs = z.object({
    text: z.string().min(1).max(2000),
});
export const ProposeActionType = z.enum(['click', 'fill', 'select', 'scroll', 'submit']);
export const ProposeActionArgs = z.object({
    selector: z.string().min(1),
    actionType: ProposeActionType,
    payload: z.string().max(2000).optional(),
    reason: z.string().max(500),
});
export const NavigateHintArgs = z.object({
    url: z.string().min(1),
    message: z.string().max(500),
});
export const FinishArgs = z.object({
    summary: z.string().max(1000).optional(),
});
/**
 * Server-side tool. The orchestrator handles `request_human` directly:
 * it never dispatches a `tool_call` frame for this name to the widget.
 * Defined here so the schema stays in one place.
 */
export const RequestHumanArgs = z.object({
    reason: z.string().min(1).max(500),
});
export const TOOL_NAMES = [
    'highlight_element',
    'explain',
    'propose_action',
    'navigate_hint',
    'finish',
    'request_human',
];
/**
 * OpenAI/OpenRouter function-calling tool spec.
 * Stored in `ai_configs.tool_definitions` so admins can tweak descriptions
 * without a redeploy.
 */
export const DEFAULT_TOOL_DEFINITIONS = [
    {
        type: 'function',
        function: {
            name: 'highlight_element',
            description: 'Highlight ANY visible element on the current page with a ring + tooltip and scroll it into view. Works on two kinds of targets: (a) interactive controls from the "Interactive elements" list, when you want to guide the user toward an action without performing it yourself, AND (b) informational content blocks from the "Content sections" list (cards, articles, sections, paragraphs), to answer "where can I read about X" / "show me the Y section" questions by visually pointing at the relevant block on the page.',
            parameters: {
                type: 'object',
                properties: {
                    selector: {
                        type: 'string',
                        description: 'Use the EXACT selector value from either the current "Interactive elements" list OR the current "Content sections" list. Do not invent selectors.',
                    },
                    label: { type: 'string', description: 'Short label shown next to the highlight (optional).' },
                    instruction: { type: 'string', description: 'One-sentence instruction or explanation shown in the chat bubble. For info-block highlights this should briefly summarize what the user is looking at.' },
                },
                required: ['selector', 'instruction'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'explain',
            description: 'Send a plain-text chat message to the user. Use for context, summaries, or follow-up questions.',
            parameters: {
                type: 'object',
                properties: { text: { type: 'string' } },
                required: ['text'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'propose_action',
            description: 'Ask the user for permission to perform an action on their behalf (click, fill, select, scroll, submit). Use this when the user explicitly asks you to do something for them and the target element is in the current Interactive elements list. Treat page features implemented by forms/buttons as actionable workflows: fill fields, click buttons, and submit forms instead of saying you lack that built-in ability. The widget will show an Allow/Deny prompt and only execute on Allow.',
            parameters: {
                type: 'object',
                properties: {
                    selector: {
                        type: 'string',
                        description: 'Use the exact selector value from the current "Interactive elements" list. NEVER use a selector from "Content sections" - those are read-only informational blocks; if you only want to draw attention to one of them, call highlight_element instead.',
                    },
                    actionType: { type: 'string', enum: ['click', 'fill', 'select', 'scroll', 'submit'] },
                    payload: { type: 'string', description: 'Required for fill/select. Ignored otherwise.' },
                    reason: {
                        type: 'string',
                        description: 'One-sentence teaching hint shown before the action runs. Explain why this step helps the user complete their task, e.g. "This sends the prompt to the image generator" rather than only "click Generate".',
                    },
                },
                required: ['selector', 'actionType', 'reason'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'navigate_hint',
            description: 'Suggest navigating to a URL within the same site (including its other subdomains, e.g. app.example.com while on example.com). Use this when the user-requested task requires another page and the URL appears under "Available links on this site". Pass that exact href - it may be a path or an absolute URL on another subdomain. After an approved navigation, stop the current turn; a continuation turn will receive the new page context. The widget will show an Allow/Deny prompt before navigating.',
            parameters: {
                type: 'object',
                properties: {
                    url: { type: 'string' },
                    message: { type: 'string', description: 'Why this navigation is needed.' },
                },
                required: ['url', 'message'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'finish',
            description: 'Call when the user has completed the requested task or no further action is needed.',
            parameters: {
                type: 'object',
                properties: { summary: { type: 'string' } },
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'request_human',
            description: 'Hand the conversation off to a human support agent. Call this when the visitor explicitly asks to talk to a person/human/real agent/support, when they say Bruno is not helping, or when you are repeatedly unable to resolve their request. Do NOT call this for greetings or curiosity questions. The server handles the rest - you should stop calling other tools after this and end the turn.',
            parameters: {
                type: 'object',
                properties: {
                    reason: {
                        type: 'string',
                        description: 'Short summary of why the visitor needs a human. Will be shown to the receiving agent.',
                    },
                },
                required: ['reason'],
            },
        },
    },
];
//# sourceMappingURL=tools.js.map